/* eslint-disable react/no-unstable-nested-components --
 * CsvDiffTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。
 */
import { type SplitSupplyBusiness } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import {
  type SplitSupplyOwnNoticeBlankPoint,
  type SplitSupplyOwnNoticeImportItem,
  SplitSupplyOwnNoticeImportPointKind,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import type { PreviewOwnSplitSupplyNoticeImportResponse } from '@ebalance/proto/bffuser/v1/split_supply_plan_service_pb'
import { useTranslation } from 'react-i18next'

import { Alert, Flex, Text } from '@enechain/ecloud-designsystem'

import { CsvDiffTable } from '~/components/common/CsvUpload/CsvDiffTable'
import { HintTooltip } from '~/components/common/HintTooltip'
import { convertAreaLabel } from '~/models/area'
import { protoDateToCivilDate } from '~/models/civilDate'
import {
  ImportErrorsSection,
  ImportIgnoredNote,
  ImportSkippedSection,
  NO_VALUE,
  PointCountCell,
  importPeriodLabel,
} from '~/pages/SplitSupplyPlan/components/ImportResultSections'
import {
  OWN_IMPORT_ISSUE_REASON_KEYS,
  OWN_IMPORT_KEY_PREFIX,
  OWN_SKIP_REASON_KEYS,
} from '~/pages/SplitSupplyPlan/models/ownImport'

type Props = {
  result: PreviewOwnSplitSupplyNoticeImportResponse
  /**
   * 誤りのあるファイルを外して確認し直す。 外せるファイルが無い (誤りのファイル名が選んだファイルと
   * 一致しない・外すと何も残らない) 時は undefined で、 ボタンを出さない。
   */
  onRemoveBadFiles: (() => void) | undefined
}

const PREFIX = OWN_IMPORT_KEY_PREFIX

const businessLabel = (b: SplitSupplyBusiness | undefined): string => {
  if (b === undefined) return NO_VALUE
  return b.businessName === ''
    ? b.businessCode
    : `${b.businessCode} ${b.businessName}`
}

const noticeKey = (row: SplitSupplyOwnNoticeImportItem): string =>
  `${row.section}-${row.targetDate?.year ?? 0}-${row.targetDate?.month ?? 0}-${row.targetDate?.day ?? 0}-${row.areaCode}-${row.ownBusiness?.businessMasterId ?? ''}-${row.loadFollowing?.businessMasterId ?? ''}-${row.loadFollowingContractId}`

const pointsOfKind = (
  row: SplitSupplyOwnNoticeImportItem,
  kind: SplitSupplyOwnNoticeImportPointKind,
): string[] =>
  row.points
    .filter((p) => p.kind === kind)
    .map((p) => `${p.supplyPointSpecificNumber} ${p.customerName}`.trim())

/**
 * 自社の通告値の取込の確認画面 (取込ダイアログの 2 段目)。 backend の preview が返した内容をそのまま見せる:
 * 取り込む通 (新規 / 上書きの地点・ファイルの日数)、 取り込まない地点、 全部空で取り込まない列・日、
 * 無視したファイル、 ファイルの誤り。 ファイルに無い日がある上書きと、 全部空の列・日があった時は、
 * 「今の値のまま」 であることと空欄に戻す方法を知らせる。 誤り・取り込める地点が無い・上限超えの
 * どれかがあれば取り込めない (呼び出し側が 「取り込む」 を押せなくする)。
 */
export const OwnImportConfirmation = ({
  result,
  onRemoveBadFiles,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const monthLabel = (year: number, month: number): string =>
    t('page:splitSupplyPlan.monthLabel', { year, month })

  if (result.tooLarge) {
    return (
      <Alert
        description={t(
          'page:splitSupplyPlan.serverError.reason.ownNoticeImportTooLarge',
        )}
        status="error"
      />
    )
  }

  const hasErrors = result.errorCount > 0
  const absentDayPoints = result.notices.reduce(
    (sum, n) => sum + n.overwriteWithAbsentDaysCount,
    0,
  )
  const blankDays = result.notices.reduce((sum, n) => sum + n.blankDayCount, 0)
  const hasBlank = result.blankPointCount > 0 || blankDays > 0
  return (
    <Flex flexDirection="column" gap={4}>
      {hasErrors && (
        <ImportErrorsSection
          errorCount={result.errorCount}
          errors={result.errors}
          invalidMessage={t(
            'page:splitSupplyPlan.serverError.reason.ownNoticeImportInvalid',
          )}
          onRemoveBadFiles={onRemoveBadFiles}
          prefix={PREFIX}
          reasonKeys={OWN_IMPORT_ISSUE_REASON_KEYS}
        />
      )}

      {!hasErrors && result.nothing && (
        <Alert
          description={t(
            'page:splitSupplyPlan.serverError.reason.ownNoticeImportNothing',
          )}
          status="warning"
        />
      )}

      {!hasErrors && absentDayPoints > 0 && (
        <Alert
          description={t(`${PREFIX}.absentDaysNotice`, {
            count: absentDayPoints,
          })}
          status="info"
        />
      )}

      {!hasErrors && hasBlank && (
        <Alert
          description={t(`${PREFIX}.blankNotice`, {
            points: result.blankPointCount,
            days: blankDays,
          })}
          status="info"
        />
      )}

      {!hasErrors && result.notices.length > 0 && (
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="bodyBold">{t(`${PREFIX}.noticesHeading`)}</Text>
          <CsvDiffTable
            columns={[
              {
                key: 'period',
                header: t(`${PREFIX}.column.period`),
                cell: (row: SplitSupplyOwnNoticeImportItem) =>
                  importPeriodLabel(
                    row.section,
                    protoDateToCivilDate(row.targetDate),
                    monthLabel,
                  ),
              },
              {
                key: 'area',
                header: t(`${PREFIX}.column.area`),
                cell: (row) => convertAreaLabel(row.areaCode),
              },
              {
                key: 'ownBusiness',
                header: t(`${PREFIX}.column.ownBusiness`),
                cell: (row) => businessLabel(row.ownBusiness),
              },
              {
                key: 'loadFollowing',
                header: t(`${PREFIX}.column.loadFollowing`),
                cell: (row) => businessLabel(row.loadFollowing),
              },
              {
                key: 'contractId',
                header: t(`${PREFIX}.column.contractId`),
                mono: true,
                cell: (row) => row.loadFollowingContractId,
              },
              {
                key: 'new',
                header: t(`${PREFIX}.column.newPoints`),
                align: 'right',
                cell: (row) => (
                  <PointCountCell
                    count={row.newPointCount}
                    points={pointsOfKind(
                      row,
                      SplitSupplyOwnNoticeImportPointKind.NEW,
                    )}
                  />
                ),
              },
              {
                key: 'overwrite',
                header: t(`${PREFIX}.column.overwritePoints`),
                align: 'right',
                cell: (row) => (
                  <PointCountCell
                    count={row.overwritePointCount}
                    points={pointsOfKind(
                      row,
                      SplitSupplyOwnNoticeImportPointKind.OVERWRITE,
                    )}
                  />
                ),
              },
              {
                key: 'days',
                header: t(`${PREFIX}.column.days`),
                align: 'right',
                cell: (row) => t(`${PREFIX}.dayCount`, { count: row.dayCount }),
              },
              {
                key: 'absent',
                header: (
                  <HintTooltip content={t(`${PREFIX}.columnHint.absent`)}>
                    <span>{t(`${PREFIX}.column.absent`)}</span>
                  </HintTooltip>
                ),
                align: 'right',
                cell: (row) =>
                  t('page:splitSupplyPlan.pointCount', {
                    count: row.overwriteWithAbsentDaysCount,
                  }),
              },
              {
                key: 'blankDays',
                header: (
                  <HintTooltip content={t(`${PREFIX}.columnHint.blankDays`)}>
                    <span>{t(`${PREFIX}.column.blankDays`)}</span>
                  </HintTooltip>
                ),
                align: 'right',
                cell: (row) =>
                  t(`${PREFIX}.dayCount`, { count: row.blankDayCount }),
              },
            ]}
            getRowKey={noticeKey}
            getRowTone={(row) =>
              row.overwritePointCount > 0 ? 'changed' : 'new'
            }
            rows={result.notices}
          />
        </Flex>
      )}

      {!hasErrors && result.blankPointCount > 0 && (
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="bodyBold">
            {t(`${PREFIX}.blank.heading`, { count: result.blankPointCount })}
          </Text>
          <CsvDiffTable
            columns={[
              {
                key: 'file',
                header: t(`${PREFIX}.column.file`),
                cell: (row: SplitSupplyOwnNoticeBlankPoint) => row.fileName,
              },
              {
                key: 'point',
                header: t(`${PREFIX}.column.supplyPoint`),
                mono: true,
                cell: (row) => row.supplyPointNumber,
              },
            ]}
            getRowKey={(row) => `${row.fileName}|${row.supplyPointNumber}`}
            getRowTone={() => undefined}
            rows={result.blankPoints}
          />
          {result.blankPoints.length < result.blankPointCount && (
            <Text textStyle="caption" color="Text.midEmphasis">
              {t(`${PREFIX}.blank.truncated`, {
                shown: result.blankPoints.length,
              })}
            </Text>
          )}
        </Flex>
      )}

      {!hasErrors && result.skippedPointCount > 0 && (
        <ImportSkippedSection
          prefix={PREFIX}
          reasonKeys={OWN_SKIP_REASON_KEYS}
          skippedCounts={result.skippedCounts}
          skippedPointCount={result.skippedPointCount}
          skippedPoints={result.skippedPoints}
        />
      )}

      <ImportIgnoredNote count={result.ignoredFileCount} prefix={PREFIX} />
    </Flex>
  )
}
